import { hasAccessToContent } from 'app/frontend/base/components/ProtectedContent'
import { uploadGuildFile } from 'app/frontend/base/api/guild'
import { useGuild } from 'app/frontend/base/config/context/guild'
import {
  useGuildOverviewData,
  usePageInfo,
} from 'app/frontend/base/config/queries'
import OverviewPage from 'app/frontend/base/pages/shared/Overview/OverviewPage'
import { OverviewTile } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamOverview/defaults'
import OverviewTileType from 'app/frontend/types/custom/OverviewTileType'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import LinkButton from '@/components/ui/link-button'
import Section from 'app/frontend/base/components/Section'
import GuildOnboardingCta from '../../components/GuildOnboardingCta'

const allowedGuildTileTypes: readonly OverviewTileType[] = [
  'Tile::CustomImage',
  'Tile::CustomLinks',
  'Tile::CustomText',
  'Tile::GuildMessage',
  'Tile::RecentNews',
  'Tile::SidebarTiles',
  'Tile::TeamSummary',
]

const GuildOverview = () => {
  const { guild } = useGuild()
  const { t } = useTranslation()
  const { data: overviewData, isLoading: isOverviewDataLoading } =
    useGuildOverviewData(guild?.id)
  const {
    data: { user },
  } = usePageInfo()

  const defaultTiles = useMemo<OverviewTile[]>(
    () => [
      {
        type: 'Tile::TeamSummary',
        position: 'main',
        accessLevel: 'none',
        order: 0,
        config: {
          title: t('overview.tiles.team-summary.default-title'),
        },
      },
      {
        type: 'Tile::RecentNews',
        position: 'main',
        accessLevel: 'none',
        order: 1,
        config: {
          title: t('overview.tiles.recent-news.default-title'),
          amount: 2,
          compact: true,
          show_view_all: true,
        },
      },
      {
        type: 'Tile::GuildMessage',
        position: 'sidebar',
        accessLevel: 'none',
        order: 0,
        config: {
          title: t('overview.tiles.guild-message.default-title'),
        },
      },
    ],
    [t],
  )

  if (!guild) return null

  if (guild.teamReferences.length === 0) {
    return (
      <Section title={guild.name}>
        <GuildOnboardingCta guild={guild} />
      </Section>
    )
  }

  const hasCharacterInGuild =
    user?.teamReferences.some((teamReference) =>
      guild.teamReferences.some(
        (guildTeamReference) => guildTeamReference.id === teamReference.id,
      ),
    ) ?? false
  const showApplyButton =
    !!user &&
    !hasCharacterInGuild &&
    guild.settings.displayApplyButtonInOverview.value

  const { hasAccess: isAdmin } = hasAccessToContent(
    'admin',
    'base',
    guild,
    undefined,
    true,
  )

  return (
    <OverviewPage
      title={guild.name}
      entity={guild}
      entityType='guild'
      isAdmin={isAdmin}
      defaultTiles={defaultTiles}
      allowedTileTypes={allowedGuildTileTypes}
      runtime={{
        overviewData,
        isOverviewDataLoading,
        ownerPath: guild.path,
        ownerName: guild.name,
        guildMotd: guild.motd,
        requireAllTeams: true,
        uploadFile: async (file) => (await uploadGuildFile(guild.id, file)).url,
        recentNewsEditPermission: {
          section: 'base',
          requirement: 'admin',
          requireAllTeams: true,
        },
      }}
      sidebarContent={
        showApplyButton ? (
          <LinkButton
            to={`${guild.path}/apply`}
            className='w-full mb-4'
            variant={'default'}
          >
            Apply to guild
          </LinkButton>
        ) : undefined
      }
    />
  )
}

export default GuildOverview
